import React, { useEffect, useRef, useState } from 'react';

const SCRIPT_ID = 'google-recaptcha-script';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

const loadReCaptchaScript = () => {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID);

    if (existingScript) {
      if (existingScript.getAttribute('data-loaded') === 'true') {
        resolve();
        return;
      }

      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    script.addEventListener('load', () => {
      script.setAttribute('data-loaded', 'true');
      resolve();
    }, { once: true });

    script.addEventListener('error', reject, { once: true });

    document.body.appendChild(script);
  });
};

const ReCaptchaWidget = ({
  siteKey,
  onVerify,
  onExpire,
  onError,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onVerify, onExpire, onError]);

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      return undefined;
    }

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.grecaptcha?.render || widgetIdRef.current !== null) {
        return;
      }

      try {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerifyRef.current?.(token),
          'expired-callback': () => onExpireRef.current?.(),
          'error-callback': () => {
            const errorMessage = 'reCAPTCHA could not be completed.';
            setLoadError(errorMessage);
            onErrorRef.current?.(errorMessage);
          },
        });
      } catch (error) {
        const errorMessage = error?.message || 'Failed to render reCAPTCHA.';
        setLoadError(errorMessage);
        onErrorRef.current?.(errorMessage);
      }
    };

    loadReCaptchaScript()
      .then(() => {
        if (cancelled) {
          return;
        }

        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(renderWidget);
          return;
        }

        renderWidget();
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        const errorMessage = error?.message || 'Failed to load reCAPTCHA.';
        setLoadError(errorMessage);
        onErrorRef.current?.(errorMessage);
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <div>
      <div ref={containerRef}></div>
      {loadError && (
        <small className="text-danger d-block mt-2">
          {loadError}
        </small>
      )}
    </div>
  );
};

export default ReCaptchaWidget;
